import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { Search } from 'lucide-react';
import cc from 'currency-codes';

import { config } from '../../utils/config';
import ClubbieModal from '../Modal/ClubbieModal';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import ClubbieSelect from '../ClubbieSelect/ClubbieSelect';
import Checkbox from '../Checkbox/Checkbox';
import DatePicker from '../DatePicker/DatePicker';

const CURRENCY_SYMBOLS = {
  GBP: '£', USD: '$', EUR: '€', AUD: 'A$', CAD: 'C$', CHF: 'Fr', JPY: '¥',
  INR: '₹', CNY: '¥', KRW: '₩', BRL: 'R$', ZAR: 'R', TRY: '₺', PLN: 'zł',
  SEK: 'kr', NOK: 'kr', DKK: 'kr', HKD: 'HK$', SGD: 'S$', NZD: 'NZ$',
  MXN: 'Mex$', AED: 'د.إ', SAR: '﷼', QAR: '﷼', KWD: 'د.ك', BHD: '.د.ب',
  OMR: '﷼', BAM: 'KM', HRK: 'kn', RSD: 'din', THB: '฿', PHP: '₱',
  MYR: 'RM', IDR: 'Rp', VND: '₫', CZK: 'Kč', HUF: 'Ft', RON: 'lei',
  BGN: 'лв', ILS: '₪', TWD: 'NT$', CLP: 'CL$', COP: 'COL$', PEN: 'S/',
  ARS: 'AR$', EGP: 'E£', NGN: '₦', KES: 'KSh', GHS: 'GH₵',
};

const CURRENCY_OPTIONS = cc.codes().map((code) => ({
  code,
  label: `${code}`,
}));

const STEP_LABELS = ['Payment details', 'Review', 'Recipients'];

/**
 * RecipientSelection — inline team list with search + select all
 */
const RecipientSelection = ({ tournamentTeams, selectedMembers, onToggle, onSelectAll }) => {
  const [search, setSearch] = useState('');

  const filtered = tournamentTeams.filter((t) =>
    t.title.toLowerCase().includes(search.toLowerCase())
  );

  const allFilteredSelected = filtered.length > 0 && filtered.every((t) => selectedMembers.includes(t.id));

  return (
    <div className="space-y-4">
      <h2 className="text-[14px] font-normal text-clubbie-white">Who do you want to send the payment request to?</h2>

      {tournamentTeams.length === 0 ? (
        <div className="bg-app-surface rounded-2xl p-8 text-center border border-app-border">
          <p className="text-[#7F8585]">No tournament teams found.</p>
          <p className="text-[#7F8585] text-sm mt-2">Please make sure you have selected a tournament with registered teams.</p>
        </div>
      ) : (
        <div className="overflow-hidden space-y-4">
          <div className="flex items-center justify-between gap-3">
            {/* Search */}
            <div className="relative md:w-80 ">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <Search size={18} />
              </div>
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-app-surface border border-app-surface rounded-3xl pl-9 pr-4 py-2.5 text-[14px] text-[#FEFCEB] placeholder-app-text-secondary focus:border-[#00E7FF] outline-none transition-colors"
              />
              
            </div>

            {/* Select All */}
            <div
                className="flex items-center gap-3 px-3 py-3 cursor-pointer"
                onClick={() => onSelectAll(filtered)}
              >
                <button onClick={() => onSelectAll(filtered)} className="text-app-text-secondary hover:text-[#FEFCEB] text-[13px] transition-colors underline">{allFilteredSelected ? 'Unselect all' : 'Select all'}</button>
            </div>
          </div>

          {/* Team list */}
          <div className="max-h-[250px] overflow-y-auto border border-[#192634] rounded-[20px] ">
          {filtered.length > 0 ? (
            filtered.map((team) => {
              const isSelected = selectedMembers.includes(team.id);
              return (
                <div
                  key={team.id}
                  className="flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors border-b border-[#192634] last:border-b-0 hover:bg-app-hover"
                  onClick={() => onToggle(team.id)}
                >
                  <Checkbox checked={isSelected} onChange={() => {}} />
                  <div className="w-6 h-6 rounded-full shrink-0 overflow-hidden border border-[#192634] bg-[#082441]">
                    {team.crest ? (
                      <img src={team.crest} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#FEFCEB] text-[10px] font-bold">
                        {team.title?.[0]?.toUpperCase() || 'T'}
                      </div>
                    )}
                  </div>
                  <span className="text-sm font-medium text-[#FEFCEB]">{team.title}</span>
                </div>
              );
            })
          ) : (
            <div className="px-3 py-3 text-[#7F8585] text-sm">No teams found</div>
          )}
          </div>
      </div>
      )}
    </div>
  );
};

/**
 * CreatePaymentModal — 3-step payment creation wizard in a modal.
 */
const CreatePaymentModal = ({ isOpen, onClose, onSuccess }) => {
  const { selectedTournament, selectedCategory } = useSelector((state) => state.tournament);

  const [creating, setCreating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const [formData, setFormData] = useState({
    paymentDescription: '',
    lineItems: [{ description: '', amount: '', currency: 'GBP' }],
    selectedMembers: [],
    whoPaysTransactionFees: 'player',
    currency: 'GBP',
    dueDate: new Date().toISOString().split('T')[0],
  });

  const [feeBreakdown, setFeeBreakdown] = useState({
    stripeFee: 0, clubbieFee: 0, totalToPay: 0, teamWillReceive: 0,
  });

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setCreating(false);
      setFormData({
        paymentDescription: '',
        lineItems: [{ description: '', amount: '', currency: 'GBP' }],
        selectedMembers: [],
        whoPaysTransactionFees: 'player',
        currency: 'GBP',
        dueDate: new Date().toISOString().split('T')[0],
      });
      setFeeBreakdown({ stripeFee: 0, clubbieFee: 0, totalToPay: 0, teamWillReceive: 0 });
    }
  }, [isOpen]);

  // Derive tournament teams
  const tournamentTeams = useMemo(() => {
    if (!selectedCategory?.teams?.length) return [];
    return selectedCategory.teams
      .filter((t) => t.status === 'ACCEPTED')
      .map((t) => {
        let teamId = t.teamId?.id || t.teamId?._id;
        if (typeof teamId === 'object' && teamId !== null) teamId = teamId.toString();
        if (!teamId && typeof t.teamId === 'string') teamId = t.teamId;
        return {
          id: teamId || '',
          title: t.teamId?.title || 'Unknown Team',
          crest: t.teamId?.crest || null,
        };
      }).filter(t => t.id);
  }, [selectedCategory]);

  // Calculate fees when on review step
  useEffect(() => {
    if (currentStep === 1) {
      const total = calculateTotal();
      setFeeBreakdown({ stripeFee: 0, clubbieFee: 0, totalToPay: Math.max(total, 0), teamWillReceive: 0 });
    }
  }, [currentStep, formData.lineItems]);

  const toggleMemberSelection = (memberId) => {
    setFormData(prev => ({
      ...prev,
      selectedMembers: prev.selectedMembers.includes(memberId)
        ? prev.selectedMembers.filter(id => id !== memberId)
        : [...prev.selectedMembers, memberId],
    }));
  };

  const handleSelectAll = (filteredTeams) => {
    const filteredIds = filteredTeams.map(t => t.id);
    const allSelected = filteredIds.every(id => formData.selectedMembers.includes(id));
    setFormData(prev => ({
      ...prev,
      selectedMembers: allSelected
        ? prev.selectedMembers.filter(id => !filteredIds.includes(id))
        : [...new Set([...prev.selectedMembers, ...filteredIds])],
    }));
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', amount: '', currency: 'GBP' }],
    }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => i === index ? { ...item, [field]: value } : item),
    }));
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index),
    }));
  };

  const calculateTotal = () => {
    return formData.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const canProceedStep1 = () => {
    return formData.paymentDescription.trim() &&
      formData.lineItems.length > 0 &&
      formData.lineItems.every(item => item.description && item.amount > 0);
  };

  const canProceedStep2 = () => {
    return calculateTotal() > 0;
  };

  const handleCreatePayment = async () => {
    try {
      if (formData.lineItems.length === 0 || formData.selectedMembers.length === 0) {
        toast.error('Please complete all required fields');
        return;
      }

      const total = calculateTotal();
      if (total <= 0) {
        toast.error('Total amount must be greater than 0');
        return;
      }

      setCreating(true);

      const accessString = localStorage.getItem('access');
      const token = accessString ? JSON.parse(accessString).token : null;

      const teamIds = formData.selectedMembers.map(id => typeof id === 'string' ? id : String(id));

      const payload = {
        tournamentId: selectedTournament?.id || selectedTournament?._id,
        categoryId: selectedCategory?._id,
        teamIds,
        amount: String(total),
        currency: formData.lineItems[0]?.currency || formData.currency || 'GBP',
        customReference: formData.paymentDescription || 'Tournament Payment',
        paymentItems: formData.lineItems,
        feePayer: formData.whoPaysTransactionFees,
        recurrence: 'one-time',
        firstBillingDate: formData.dueDate || new Date().toISOString(),
      };

      const response = await fetch(`${config.API_BASE_URL}/${config.API_PATH}/payments/create-tournament-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'ngrok-skip-browser-warning': 'true',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to create payment');
      }

      toast.success('Payment created successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to create payment. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const isLastStep = currentStep === STEP_LABELS.length - 1;
  const hasNoTeams = tournamentTeams.length === 0;

  // Footer
  const footer = hasNoTeams ? (
    <div className="flex items-center gap-3 w-full justify-end">
      <button
        type="button"
        className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
        onClick={onClose}
      >
        Close
      </button>
    </div>
  ) : (
    <div className="flex items-center gap-3 w-full">
      <button
        type="button"
        className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
        onClick={onClose}
      >
        Cancel
      </button>
      <div className="ml-auto flex items-center gap-3">
        {currentStep > 0 && (
          <button
            type="button"
            className="px-6 py-3 text-sm font-medium text-app-text bg-app-card rounded-full hover:text-[#FEFCEB] transition-colors cursor-pointer"
            onClick={() => setCurrentStep(currentStep - 1)}
          >
            Back
          </button>
        )}
        {isLastStep ? (
          <button
            type="button"
            className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            onClick={handleCreatePayment}
            disabled={formData.selectedMembers.length === 0 || creating}
          >
            {creating ? <><LoadingSpinner size="small" /><span>Creating...</span></> : 'Create'}
          </button>
        ) : (
          <button
            type="button"
            className="bg-[#00E7FF] hover:opacity-50 text-[#021529] font-medium px-6 py-3 rounded-full text-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={
              (currentStep === 0 && !canProceedStep1()) ||
              (currentStep === 1 && !canProceedStep2())
            }
          >
            Next
          </button>
        )}
      </div>
    </div>
  );

  return (
    <ClubbieModal
      isOpen={isOpen}
      width="600px"
      maxHeight="85vh"
      toggle={onClose}
      title="Create payment"
      footer={footer}
    >
      {hasNoTeams ? (
        <div className="bg-app-surface rounded-2xl p-8 text-center border border-app-border">
          <p className="text-[#7F8585]">No tournament teams found.</p>
          <p className="text-[#7F8585] text-sm mt-2">
            Please make sure you have selected a tournament with registered teams.
          </p>
        </div>
      ) : (
        <>
      {/* Clickable Stepper */}
      <div className="flex items-center gap-3 mb-5">
        {STEP_LABELS.map((label, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => {
              if (idx < currentStep) setCurrentStep(idx);
              if (idx === 1 && currentStep === 0 && canProceedStep1()) setCurrentStep(1);
              if (idx === 2 && currentStep === 1 && canProceedStep2()) setCurrentStep(2);
            }}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                idx <= currentStep ? 'bg-[#00E7FF] text-[#000F1F]' : 'bg-[#192634] text-[#7F8585]'
              }`}
            >
              {idx < currentStep ? (
                <svg className="w-3 h-3" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : (
                idx + 1
              )}
            </div>
            <span className={`text-[13px] font-medium transition-colors ${idx === currentStep ? 'text-[#FEFCEB]' : 'text-[#7F8585]'}`}>
              {label}
            </span>
            {idx < STEP_LABELS.length - 1 && <div className="w-4 h-px bg-[#192634]" />}
          </div>
        ))}
      </div>

      <div className="space-y-4">
        {/* ==================== STEP 0: PAYMENT DETAILS ==================== */}
        {currentStep === 0 && (
          <div className="space-y-4">
            {/* Payment Description */}
            <div className="space-y-2">
              <label className="block text-[14px] font-normal text-clubbie-white">Payment description</label>
              <input
                type="text"
                value={formData.paymentDescription}
                onChange={(e) => setFormData({ ...formData, paymentDescription: e.target.value })}
                placeholder="E.g. Annual team subscription"
                className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
              />
            </div>

            {/* Breakdown / Line Items */}
            <div className="space-y-4">
              <label className="block text-[14px] font-normal text-clubbie-white">Breakdown</label>
              <div className="space-y-6">
                {formData.lineItems.map((item, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <label className="text-[14px] font-normal text-clubbie-white">Item {index + 1}</label>
                      {formData.lineItems.length > 1 && (
                        <button onClick={() => removeLineItem(index)} className="text-[#7F8585] hover:text-red-400 transition-colors text-sm">
                          Remove
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                      placeholder="E.g. Match fees"
                      className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors"
                    />
                    <div className="flex gap-2">
                      <div className="shrink-0">
                        <ClubbieSelect
                          placeholder="Currency"
                          options={CURRENCY_OPTIONS}
                          value={CURRENCY_OPTIONS.find((o) => o.code === (item.currency || 'GBP')) || null}
                          onChange={(option) => updateLineItem(index, 'currency', option.code)}
                          getOptionLabel={(o) => o?.label || ''}
                          getOptionValue={(o) => o?.code || ''}
                          isSearchable
                          dropdownMinWidth={280}
                        />
                      </div>
                      <div className="relative flex flex-1 items-center justify-start bg-app-surface border border-app-surface rounded-3xl pl-4 focus-within:border-blue-shade-2 transition-colors">
                        <div className="text-[#FEFCEB] font-medium text-sm">
                          {CURRENCY_SYMBOLS[item.currency || 'GBP'] || item.currency || '£'}
                        </div>
                        <input
                          type="number"
                          value={item.amount}
                          onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                          placeholder="0.00"
                          className="flex bg-transparent pl-2 pr-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={addLineItem}
                className="w-full bg-app-card hover:bg-app-hover text-[#FEFCEB] py-2.5 rounded-3xl font-medium transition-colors flex items-center justify-center gap-2 border border-app-surface"
              >
                <span className="text-xl">+</span> Add line item
              </button>
            </div>

            {formData.lineItems.length > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-[#FEFCEB] font-medium">Total</span>
                <span className="text-[#FEFCEB] font-medium">{CURRENCY_SYMBOLS[formData.lineItems[0]?.currency] || formData.lineItems[0]?.currency || '£'} {calculateTotal().toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        {/* ==================== STEP 1: REVIEW ==================== */}
        {currentStep === 1 && (
          <div className="space-y-4">
            {/* Due Date */}
            <div className="space-y-2">
              <label className="block text-[14px] font-normal text-clubbie-white">Due date</label>
              <DatePicker
                value={formData.dueDate}
                onChange={(date) => setFormData((prev) => ({ ...prev, dueDate: date }))}
                disablePastDates={true}
              />
            </div>

            {/* Payment Breakdown */}
            <div className="space-y-2">
              <label className="block text-[14px] font-normal text-clubbie-white">Payment breakdown</label>
              <div className="bg-app-surface rounded-2xl p-4 space-y-4">
                <div className="space-y-3">
                  {formData.lineItems.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-app-text-secondary">{item.description || `Item ${index + 1}`}</span>
                      <span className="text-app-text-secondary">{CURRENCY_SYMBOLS[item.currency] || item.currency || '£'} {parseFloat(item.amount || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-2 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[#FEFCEB] font-medium">Total</span>
                    <span className="text-[#FEFCEB] font-medium">{CURRENCY_SYMBOLS[formData.lineItems[0]?.currency] || formData.lineItems[0]?.currency || '£'} {feeBreakdown.totalToPay.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== STEP 2: RECIPIENTS ==================== */}
        {currentStep === 2 && (
          <RecipientSelection
            tournamentTeams={tournamentTeams}
            selectedMembers={formData.selectedMembers}
            onToggle={toggleMemberSelection}
            onSelectAll={handleSelectAll}
          />
        )}
      </div>
        </>
      )}
    </ClubbieModal>
  );
};

export default CreatePaymentModal;
